<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';

import { defaultOutgoingProperty } from '@/helpers/builder';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';

import OutgoingPropertyCard from './modules/OutgoingProperties/OutgoingPropertyCard.vue';
import ScenarioTypeTabs from './modules/ScenarioTypeTabs.vue';

import type { OutgoingProperty, OutgoingPropertyEntry } from '@/types';

let propertyCounter = 0;

const { loanApp, sectionStates, saveAndGoNext, goBack } = useUserQuoteProcess();
const { isCommercialQuoteFlowEnabled } = useFeatureFlags();

const showError = ref(false);
const properties = ref<{ item: OutgoingPropertyEntry }[]>(loanApp.value.outgoingProperties.map((item) => ({ item })));
const propertyRefs = useTemplateRefsList<InstanceType<typeof OutgoingPropertyCard>>();

const outgoingPropertiesType = computed(() => sectionStates.value.outgoingProperties);
const isSingle = computed(() => properties.value.length === 1);

const validateItems = async () => {
  if (propertyRefs.value.length === 0) return true;

  const validations = propertyRefs.value.map((card) => card.update());
  const results = await Promise.all(validations);

  return results.every((isValid) => isValid);
};

const addItem = async () => {
  const allValid = await validateItems();

  if (allValid) {
    showError.value = false;
    const id = `outgoing-property-${propertyCounter++}`;
    properties.value.push({ item: { id } });
  } else {
    showError.value = true;
  }
};

const removeItem = (id: string) => {
  properties.value = properties.value.filter((p) => p.item.id !== id);
};

const saveAndContinue = async () => {
  const allValid = await validateItems();

  if (allValid) {
    showError.value = false;

    const outgoingProperties: OutgoingProperty[] = properties.value.map((p) => {
      const propertyData = { ...p.item };

      if (outgoingPropertiesType.value === 'simple') {
        propertyData.address = {
          addressLine: '',
          locality: '',
          state: propertyData.address?.state || '',
          postcode: propertyData.address?.postcode || '',
          countryCode: 'AU',
        };
      }

      return Object.assign(defaultOutgoingProperty(), propertyData);
    });

    saveAndGoNext({ outgoingProperties });
  } else {
    showError.value = true;
  }
};

watch(
  properties,
  (val) => {
    if (val.length === 0) {
      const id = `outgoing-property-${propertyCounter++}`;
      properties.value.push({ item: { id } });
    }
  },
  { immediate: true },
);

watch(outgoingPropertiesType, (newType) => {
  showError.value = false;

  for (const cardRef of propertyRefs.value) {
    if (cardRef) {
      cardRef.handleModeSwitch(newType);
    }
  }
});

onMounted(() => {
  if (isCommercialQuoteFlowEnabled()) {
    saveAndGoNext({ outgoingProperties: loanApp.value.outgoingProperties });
  }
});
</script>

<template>
  <template v-if="!isCommercialQuoteFlowEnabled()">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the properties your client is
        <BxHighlight>selling</BxHighlight>
      </h2>
      <p v-if="isSingle" class="mt-2">
        Enter the details for the first property below, you can add additional properties by clicking 'Add another
        property'.
      </p>
    </div>
    <div class="section-panel md:p-6">
      <div class="mb-6">
        <ScenarioTypeTabs :section-key="'outgoingProperties'" title="Selling" />
      </div>
      <template v-for="(property, pIndx) in properties" :key="pIndx">
        <OutgoingPropertyCard
          :ref="propertyRefs.set"
          v-model="property.item"
          class="mb-4"
          is-multiple
          @remove="removeItem"
        />
      </template>
      <div class="mt-6">
        <BxButton
          id="outgoing-properties-add"
          data-test-id="outgoing-properties-add"
          variant="tertiary"
          @click="addItem()"
        >
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add another property
        </BxButton>
      </div>
      <div class="mt-6">
        <BxErrorPanel v-if="showError" class="mb-3"> Please review all provided information.</BxErrorPanel>
        <BxButton
          id="outgoing-properties-continue"
          data-test-id="outgoing-properties-continue"
          variant="primary"
          @click="saveAndContinue()"
        >
          Save and continue
        </BxButton>
        <BxButton data-test-id="outgoing-properties-back" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
      </div>
    </div>
  </template>
</template>
